import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Input } from '@design-system/components/input/input';
import { Typography } from '@design-system/components/typography/typography';
import { hintItem, wrapper, hintsContainer } from './titleInputWithHints.css';
import axios from 'axios';
import auth from '@client/components/auth';
import { debounce, isEmpty } from 'lodash';

type Hint = { title: string; description?: string; uuid: string };

type TitleInputWithHintsProps = {
  required?: boolean;
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: string;
  onUpdate: (description: string, uuid: string) => void;
};

const getExerciseList = (exerciseName: string) =>
  axios.get(
    `${
      import.meta.env.VITE_MUX_API_URL
    }/exercise-library?exerciseName=${exerciseName}`,
    {
      headers: {
        Authorization: auth.getToken(),
      },
    },
  );

export const TitleInputWithHints = ({
  required,
  value,
  onChange,
  disabled,
  error,
  onUpdate,
}: TitleInputWithHintsProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [initRender, setInitRender] = useState(true);
  const [inputFocus, setInputFocus] = useState(false);
  const [exerciseDB, setExerciseDB] = useState<
    Array<{ name: string; description: string; id: string }>
  >([]);

  const handleClick = ({ title, description, uuid }: Hint) => {
    onChange(title);
    if (description !== undefined) {
      onUpdate(description, uuid);
    }
    setInputFocus(false);
  };

  const handleOutsideClick = (e: any) => {
    if (wrapperRef?.current && !wrapperRef.current.contains(e.target)) {
      setInputFocus(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick, true);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [wrapperRef]);

  const onInputChange = (ev: ChangeEvent<HTMLInputElement>) => {
    onChange(ev.target.value);
    debouncedSearch(ev.target.value);
  };

  const getExerciseOptions = async (exerciseName: string) => {
    if (!exerciseName || exerciseName === '') {
      setExerciseDB([]);
      return;
    }

    const res = await getExerciseList(exerciseName);
    if (!isEmpty(res.data)) {
      setExerciseDB(res.data.data);
    }
  };

  const debouncedSearch = useRef(
    debounce((textInputValue) => {
      getExerciseOptions(textInputValue);
    }, 200),
  ).current;

  useEffect(() => {
    debouncedSearch.cancel();
    if (!initRender && !isEmpty(value)) {
      debouncedSearch(value);
    }
    if (initRender) {
      setInitRender(false);
    }
  }, [value]);

  return (
    <div className={wrapper} ref={wrapperRef}>
      <Input
        label="Title"
        required={required}
        value={value}
        onChange={onInputChange}
        disabled={disabled}
        error={error}
        autoComplete="off"
        onFocus={() => setInputFocus(true)}
      />
      {inputFocus && !isEmpty(exerciseDB) ? (
        <div className={hintsContainer}>
          {exerciseDB.map((option) => (
            <div key={option.id} className={hintItem}>
              <Typography
                onClick={() =>
                  handleClick({
                    uuid: option.id,
                    title: option.name,
                    description: option.description,
                  })
                }
              >
                {option.name}
              </Typography>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
